import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { accountApi } from '../../api/account'
import { RefusedError } from '../../api/client'
import { orgsApi } from '../../api/orgs'
import { settingsApi } from '../../api/settings'
import { ORGS_KEY } from '../../hooks/useOrg'
import { useSchedule } from '../../hooks/useSchedule'
import { useSession } from '../../hooks/useSession'
import { useSpoke } from '../../hooks/useSpoke'
import type { PasswordReset } from '../../types/account'
import type { SettingValue } from '../../types/settings'
import { Glance } from './Glance'
import { OrgsPanel } from './Orgs'
import { Sections } from './Sections'
import { Spend } from './Spend'
import './settings.css'

// TENANT SETTINGS — "allow admins to manage these tenant settings, too" (owner, 2026-09-13).
//
// Everything this deployment decided used to live in appsettings, written by Deploy-Prod, and changing
// one meant shipping a build. This screen is the same list, editable: the pack manifest's seed fields
// served with their current values, and an override saved over whatever the deploy chose.
//
// Presentation only. The API decides what a field is, what it is set to, where that value came from
// and when a change starts to matter; nothing here recomputes any of it.

const SETTINGS_KEY = 'settings'

export function Settings() {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const { user } = useSession()
  const [placing, setPlacing] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<{ key: string; message: string } | null>(null)
  // The one copy of a freshly minted temporary password. Deliberately component state and not the
  // query cache: it must not survive a refetch, a navigation or a reload.
  const [minted, setMinted] = useState<PasswordReset | null>(null)
  const [resetError, setResetError] = useState<string | null>(null)
  const isAdmin = user?.roles.some((r) => r === 'scout-admin' || r === 'spoke-admin') ?? false

  const settings = useQuery({ queryKey: [SETTINGS_KEY, spoke], queryFn: () => settingsApi.list(spoke), retry: false })
  const orgs = useQuery({ queryKey: [ORGS_KEY, spoke], queryFn: () => orgsApi.list(spoke), staleTime: 60_000 })
  const schedule = useSchedule()

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: [SETTINGS_KEY, spoke] })
    void qc.invalidateQueries({ queryKey: [ORGS_KEY, spoke] })
    void qc.invalidateQueries({ queryKey: ['landscape', spoke] })
    void qc.invalidateQueries({ queryKey: ['schedule', spoke] })
  }

  const save = useMutation({
    mutationFn: (to: { key: string; value: SettingValue }) => settingsApi.set(spoke, to.key, to.value),
    onSuccess: () => { setRefusal(null); invalidate() },
    onError: (error, to) => setRefusal({ key: to.key, message: message(error) }),
  })

  const reset = useMutation({
    mutationFn: (key: string) => settingsApi.reset(spoke, key),
    onSuccess: () => { setRefusal(null); invalidate() },
    onError: (error, key) => setRefusal({ key, message: message(error) }),
  })

  const place = useMutation({
    mutationFn: (to: { userId: string; key: string }) => orgsApi.place(spoke, to.userId, to.key),
    onSuccess: () => { setPlacing(null); invalidate() },
  })

  const resetPassword = useMutation({
    mutationFn: (userId: string) => accountApi.resetPassword(spoke, userId),
    onMutate: () => { setMinted(null); setResetError(null) },
    onSuccess: (fresh) => setMinted(fresh),
    onError: (error) => setResetError(message(error)),
  })

  const denied = settings.error instanceof RefusedError ? settings.error.refusal : null
  const fields = settings.data?.fields ?? []
  const busy = save.isPending ? save.variables?.key ?? null : reset.isPending ? reset.variables ?? null : null

  return (
    <section className="page settings">
      <header className="deals-head">
        <div>
          <p className="kicker">Firm data · tenant settings</p>
          <h1>Tenant settings</h1>
          <p className="lede">
            Everything this deployment decided about your firm, in one place and changeable here. Most of it applies
            the moment you save it; the nightly cadence, the hour it runs and how far back a first read goes take
            effect at the next run, and how people sign in takes effect at their next sign-in. Each setting says which.
          </p>
        </div>
      </header>

      {settings.isPending && <p className="muted">Loading…</p>}

      {denied && (
        <div className="card">
          <div className="refusal">
            <strong>{denied.reason === 'DenyByDefault' ? 'Not yours to change' : denied.reason}</strong>
            <p>{denied.message}</p>
          </div>
        </div>
      )}

      {settings.isSuccess && (
        <>
          <Glance
            fields={fields}
            orgs={orgs.data ?? null}
            nextDue={schedule.data?.nextDue ?? null}
            cadence={String(fields.find((f) => f.key === 'scanCadence')?.value ?? 'daily')}
          />

          {orgs.isSuccess && (
            <OrgsPanel
              orgs={orgs.data}
              placing={placing}
              pending={place.isPending ? place.variables?.userId ?? null : null}
              error={place.isError ? message(place.error) : null}
              onPlacing={setPlacing}
              onPlace={(userId, key) => place.mutate({ userId, key })}
              canReset={isAdmin}
              resetting={resetPassword.isPending ? resetPassword.variables ?? null : null}
              reset={minted}
              resetError={resetError}
              onReset={(userId) => resetPassword.mutate(userId)}
            />
          )}

          <Sections
            fields={fields}
            busy={busy}
            error={refusal}
            onSave={(key, value) => save.mutate({ key, value })}
            onReset={(key) => reset.mutate(key)}
          />

          <Spend spoke={spoke} />

          <p className="muted small settings-note">
            These are the pack&apos;s own seed fields (<code>pack/manifest.json</code>), the same list a deployment is
            asked for when Scout is installed. A setting saved here overrides what the deploy chose; resetting one puts
            the deployed value back. Nothing here is ever a credential.
          </p>
        </>
      )}
    </section>
  )
}

/** The API's own words for a refusal; its message otherwise. Never a guess at what went wrong. */
function message(error: unknown): string {
  if (error instanceof RefusedError) return error.refusal.message
  return error instanceof Error ? error.message : 'Something went wrong.'
}
